import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Search, Terminal, X as XIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  listCommands,
  listSearchProviders,
  runCommand,
  runSearchItem,
  buildCommandContext,
  parseInput,
  rankCommands,
  rankSearchItems,
  useUsageStore,
} from '@/lib/commands';
import type {
  Command,
  SearchItem,
  SearchKind,
  ParsedInput,
} from '@/lib/commands';

/**
 * Unified command + search bar behind Cmd+P (search) and Cmd+Shift+P
 * (command, input prefilled with `>`). A single input whose content
 * determines the mode:
 *   - starts with `>`  → command mode
 *   - starts with `@x` → search mode scoped to kind `x`
 *   - otherwise        → free-text search mode
 *
 * The rendered list is a unified, ranked view. When the input is empty we
 * group by section (Recent · Frequent · by kind) to tell the user why each
 * row is there; while a query is active we flatten and rank by relevance.
 */
interface CommandBarProps {
  open: boolean;
  onClose: () => void;
  /** Initial input value. Cmd+Shift+P passes `>` here to start in command mode. */
  initialInput?: string;
}

const MAX_RESULTS = 50;
const VISIBLE_ROWS = 10;
const DEFAULT_LIMIT = 10;        // how many rows show with empty input

type Row =
  | { kind: 'header'; id: string; label: string }
  | { kind: 'command'; id: string; command: Command }
  | { kind: 'item'; id: string; item: SearchItem };

export function CommandBar({ open, onClose, initialInput = '' }: CommandBarProps) {
  const [input, setInput] = useState(initialInput);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const parsed: ParsedInput = useMemo(() => parseInput(input), [input]);

  // Snapshot context + usage once per open — re-computing on every keystroke
  // would mean each character triggers a Zustand subscription round trip.
  const ctx = useMemo(() => buildCommandContext(queryClient), [queryClient, open]);
  const usageCommands = useUsageStore((s) => s.commands);
  const usageSearch = useUsageStore((s) => s.search);

  // Build the row list — heterogeneous (section headers + commands + items).
  // Selectable rows are only the non-header ones; arrow-nav skips headers.
  const rows: Row[] = useMemo(() => {
    if (!open) return [];

    if (parsed.mode === 'command') {
      const all = listCommands(ctx);
      const ranked = rankCommands(all, parsed.query, usageCommands, MAX_RESULTS);
      if (!parsed.query) return buildCommandDefaultRows(ranked, usageCommands);
      return ranked.map<Row>((c) => ({ kind: 'command', id: c.id, command: c }));
    }

    // Search mode. Providers are gated by scope; all items collapse into a
    // single ranked list scored by fuzzy + usage + per-kind boost.
    const providers = listSearchProviders().filter(
      (p) => !parsed.scope || p.kind === parsed.scope,
    );
    const allItems: SearchItem[] = [];
    for (const p of providers) {
      try {
        allItems.push(...p.list(ctx));
      } catch (err) {
        // A broken provider shouldn't wipe the whole bar — log and carry on.
        console.error('[command-bar] provider failed', p.id, err);
      }
    }
    const ranked = rankSearchItems(allItems, parsed.query, usageSearch, MAX_RESULTS);
    if (!parsed.query) return buildSearchDefaultRows(ranked, usageSearch);
    return ranked.map<Row>((i) => ({ kind: 'item', id: i.id, item: i }));
  }, [open, parsed, ctx, usageCommands, usageSearch]);

  const selectableIndexes = useMemo(
    () => rows.map((r, i) => (r.kind === 'header' ? -1 : i)).filter((i) => i >= 0),
    [rows],
  );

  // Reset input + selection when re-opened. Focus the input on mount.
  useEffect(() => {
    if (open) {
      setInput(initialInput);
      setSelectedIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open, initialInput]);

  // Initial selection points at the first selectable row. When the list
  // reshapes under the cursor, clamp sensibly.
  useEffect(() => {
    if (selectableIndexes.length === 0) return;
    if (!selectableIndexes.includes(selectedIndex)) {
      setSelectedIndex(selectableIndexes[0]);
    }
  }, [rows, selectableIndexes, selectedIndex]);

  // Keep the focused row in view while arrowing.
  useEffect(() => {
    if (!listRef.current) return;
    const row = listRef.current.children[selectedIndex] as HTMLElement | undefined;
    row?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const pickRow = async (row: Row) => {
    if (row.kind === 'header') return;
    onClose();
    try {
      if (row.kind === 'command') await runCommand(row.command, ctx);
      else await runSearchItem(row.item, ctx);
    } catch (err) {
      console.error('[command-bar] run failed', row.id, err);
    }
  };

  const moveSelection = (dir: 1 | -1) => {
    if (selectableIndexes.length === 0) return;
    const here = selectableIndexes.indexOf(selectedIndex);
    const nextIdx =
      (here + dir + selectableIndexes.length) % selectableIndexes.length;
    setSelectedIndex(selectableIndexes[nextIdx]);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveSelection(1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveSelection(-1);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[selectedIndex];
      if (row) pickRow(row);
      return;
    }
  };

  if (!open) return null;

  const modeLabel = parsed.mode === 'command' ? 'Command · ⌘⇧P' : 'Search · ⌘P';
  const ModeIcon = parsed.mode === 'command' ? Terminal : Search;
  const placeholder =
    parsed.mode === 'command'
      ? 'Type a command...'
      : parsed.scope
        ? `Search ${parsed.scope}...`
        : 'Search tables, columns, queries, tabs...';

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[18vh] animate-fade-in"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40" />

      <div
        className="relative w-[640px] max-w-[calc(100vw-16px)] max-h-[480px] bg-dbgo-modal border rounded-lg shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        {/* Input row */}
        <div className="flex items-center gap-3 px-4 py-3 border-b">
          <ModeIcon size={16} className="text-text-muted flex-shrink-0" />
          {parsed.scope && (
            <span className="text-2xs font-mono px-1.5 py-0.5 rounded-sm bg-accent-blue/15 text-accent-blue">
              @{parsed.scope}
            </span>
          )}
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setSelectedIndex(0);
            }}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-sm text-text-primary placeholder-text-muted outline-none"
            aria-autocomplete="list"
            aria-activedescendant={`cb-row-${selectedIndex}`}
          />
          <span className="text-2xs text-text-muted font-mono hidden sm:inline">
            {modeLabel}
          </span>
          <button
            onClick={onClose}
            className="w-5 h-5 flex items-center justify-center rounded-sm text-text-muted hover:text-text-secondary hover:bg-dbgo-tab-hover"
            title="Close (Esc)"
          >
            <XIcon size={14} />
          </button>
        </div>

        {/* Result list */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto"
          style={{ maxHeight: VISIBLE_ROWS * 44 }}
        >
          {rows.length === 0 && <EmptyState mode={parsed.mode} scope={parsed.scope} />}

          {rows.map((row, i) => {
            if (row.kind === 'header') {
              return (
                <div
                  key={`header-${row.id}`}
                  className="px-4 pt-3 pb-1 text-2xs uppercase tracking-widest text-text-muted select-none"
                >
                  {row.label}
                </div>
              );
            }
            const isSelected = i === selectedIndex;
            const Icon: LucideIcon =
              row.kind === 'command'
                ? row.command.icon ?? Terminal
                : row.item.icon ?? Search;
            const label = row.kind === 'command' ? row.command.label : row.item.label;
            const description =
              row.kind === 'command' ? row.command.description : row.item.subtitle;
            const shortcut = row.kind === 'command' ? row.command.shortcut : undefined;
            const categoryChip =
              row.kind === 'command' ? row.command.category : row.item.kind;
            const iconColor =
              row.kind === 'item' && row.item.tint ? row.item.tint : undefined;

            return (
              <button
                key={row.id}
                id={`cb-row-${i}`}
                role="option"
                aria-selected={isSelected}
                className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                  isSelected
                    ? 'bg-dbgo-tab-hover ring-1 ring-accent-blue/40'
                    : 'hover:bg-dbgo-tab-hover'
                }`}
                onClick={() => pickRow(row)}
                onMouseEnter={() => setSelectedIndex(i)}
              >
                <Icon
                  size={16}
                  className="text-text-secondary flex-shrink-0"
                  style={iconColor ? { color: iconColor } : undefined}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-text-primary truncate">
                    <Highlighted text={label} term={parsed.query} />
                  </div>
                  {description && (
                    <div className="text-[11px] text-text-muted truncate">
                      <Highlighted text={description} term={parsed.query} dim />
                    </div>
                  )}
                </div>
                {shortcut && (
                  <kbd className="text-2xs text-text-muted font-mono px-1.5 py-0.5 border rounded">
                    {shortcut}
                  </kbd>
                )}
                <span className="text-2xs text-text-muted font-mono px-1.5 py-0.5 rounded-sm bg-dbgo-badge">
                  {categoryChip}
                </span>
              </button>
            );
          })}
        </div>

        {/* Footer help row */}
        <div className="flex items-center gap-3 px-4 py-1.5 border-t text-2xs text-text-muted">
          <span>↑↓ Navigate</span>
          <span>↵ Open</span>
          {parsed.mode === 'command' ? (
            <span>⌫ Search mode</span>
          ) : (
            <>
              <span>&gt; Commands</span>
              <span>@tables/@columns/@tabs…</span>
            </>
          )}
          <span>Esc Close</span>
        </div>
      </div>
    </div>
  );
}

/** Cold-start commands — fallback when the user hasn't used the bar yet. */
const SEED_COMMAND_IDS = [
  'query.new',
  'ui.toggle-sidebar',
  'ui.toggle-theme',
  'ui.open-settings',
  'connection.new',
  'tunnel.new',
  'tab.switch',
  'connection.reload-schema',
  'connection.disconnect-all',
  'auth.signout',
];

function buildCommandDefaultRows(all: Command[], usage: Record<string, { count: number; lastUsedAt: number }>): Row[] {
  const rows: Row[] = [];
  const usedIds = new Set<string>();

  // Partition into recent (used in last 7d) and frequent (by count), up to
  // 10 total. Seed IDs fill any remaining slots.
  const now = Date.now();
  const withUsage = all
    .map((c) => ({ cmd: c, rec: usage[c.id] }))
    .filter((e) => !!e.rec);

  const recent = withUsage
    .filter((e) => (now - e.rec!.lastUsedAt) / 86_400_000 <= 7)
    .sort((a, b) => b.rec!.lastUsedAt - a.rec!.lastUsedAt)
    .slice(0, 6);

  const frequent = withUsage
    .filter((e) => !recent.includes(e))
    .sort((a, b) => b.rec!.count - a.rec!.count)
    .slice(0, DEFAULT_LIMIT - recent.length);

  const recentCmds = recent.map((e) => e.cmd);
  const frequentCmds = frequent.map((e) => e.cmd);
  recentCmds.forEach((c) => usedIds.add(c.id));
  frequentCmds.forEach((c) => usedIds.add(c.id));

  if (recentCmds.length > 0) {
    rows.push({ kind: 'header', id: 'recent', label: 'Recent' });
    for (const c of recentCmds) rows.push({ kind: 'command', id: c.id, command: c });
  }
  if (frequentCmds.length > 0) {
    rows.push({ kind: 'header', id: 'frequent', label: 'Frequent' });
    for (const c of frequentCmds) rows.push({ kind: 'command', id: c.id, command: c });
  }

  // Fill from the seed set to reach DEFAULT_LIMIT.
  const picked = rows.filter((r) => r.kind === 'command').length;
  if (picked < DEFAULT_LIMIT) {
    const seeds = SEED_COMMAND_IDS
      .filter((id) => !usedIds.has(id))
      .map((id) => all.find((c) => c.id === id))
      .filter((c): c is Command => !!c)
      .slice(0, DEFAULT_LIMIT - picked);
    if (seeds.length > 0) {
      rows.push({ kind: 'header', id: 'suggested', label: picked > 0 ? 'Suggested' : 'Get started' });
      for (const c of seeds) rows.push({ kind: 'command', id: c.id, command: c });
    }
  }

  return rows;
}

function buildSearchDefaultRows(
  all: SearchItem[],
  usage: Record<string, { count: number; lastUsedAt: number }>,
): Row[] {
  const rows: Row[] = [];
  const used = all.filter((i) => usage[i.id]);
  const now = Date.now();

  const recent = used
    .filter((i) => (now - usage[i.id].lastUsedAt) / 86_400_000 <= 7)
    .sort((a, b) => usage[b.id].lastUsedAt - usage[a.id].lastUsedAt)
    .slice(0, 6);

  const pickedIds = new Set(recent.map((i) => i.id));
  const frequent = used
    .filter((i) => !pickedIds.has(i.id))
    .sort((a, b) => usage[b.id].count - usage[a.id].count)
    .slice(0, DEFAULT_LIMIT - recent.length);
  frequent.forEach((i) => pickedIds.add(i.id));

  if (recent.length > 0) {
    rows.push({ kind: 'header', id: 'recent', label: 'Recent' });
    for (const i of recent) rows.push({ kind: 'item', id: i.id, item: i });
  }
  if (frequent.length > 0) {
    rows.push({ kind: 'header', id: 'frequent', label: 'Frequent' });
    for (const i of frequent) rows.push({ kind: 'item', id: i.id, item: i });
  }

  // Cold start: top tabs currently open + first few tables, so the first-
  // time user sees *something* actionable rather than an empty panel.
  const remaining = DEFAULT_LIMIT - (recent.length + frequent.length);
  if (remaining > 0) {
    const openTabs = all.filter((i) => i.kind === 'tab' && !pickedIds.has(i.id)).slice(0, Math.min(4, remaining));
    const someTables = all.filter((i) => i.kind === 'table' && !pickedIds.has(i.id)).slice(0, remaining - openTabs.length);
    const seeds = [...openTabs, ...someTables];
    if (seeds.length > 0) {
      rows.push({ kind: 'header', id: 'suggested', label: rows.length > 0 ? 'Suggested' : 'Jump to' });
      for (const i of seeds) rows.push({ kind: 'item', id: i.id, item: i });
    }
  }

  return rows;
}

function EmptyState({ mode, scope }: { mode: ParsedInput['mode']; scope: SearchKind | null }) {
  if (mode === 'command') {
    return <div className="px-4 py-8 text-center text-xs text-text-muted">No matching commands.</div>;
  }
  return (
    <div className="px-4 py-8 text-center text-xs text-text-muted">
      {scope
        ? `No ${scope} matches. Schema cache may be empty — open a connection once to populate it.`
        : 'Nothing yet. Open a connection to populate the schema cache.'}
    </div>
  );
}

/** Lightweight substring highlighter. Matches the sidebar's <mark> style. */
function Highlighted({
  text,
  term,
  dim = false,
}: {
  text: string;
  term: string;
  dim?: boolean;
}) {
  if (!term) return <>{text}</>;
  const lower = text.toLowerCase();
  const needle = term.toLowerCase();
  const idx = lower.indexOf(needle);
  if (idx === -1) return <>{text}</>;
  const className = dim
    ? 'bg-accent-yellow/20 rounded-sm px-[1px]'
    : 'bg-accent-yellow/30 text-text-primary rounded-sm px-[1px]';
  return (
    <>
      {text.slice(0, idx)}
      <mark className={className}>{text.slice(idx, idx + needle.length)}</mark>
      {text.slice(idx + needle.length)}
    </>
  );
}
